import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
} from "react";
import type { EFFeatureDefinition } from "@/features/runtime/FeatureDefinition";
import type { EFFeatureRuntimeSnapshot } from "@/features/runtime/FeatureRuntimeTypes";
import type { EFFeatureId } from "@/features/runtime/FeatureTypes";

export type FeatureStateContextValue = {
	definitions: readonly EFFeatureDefinition[];
	registry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>;
	featureSnapshots: readonly EFFeatureRuntimeSnapshot[];
};

const FeatureStateContext = createContext<FeatureStateContextValue | null>(null);

export function FeatureStateProvider({
	value,
	children,
}: {
	value: FeatureStateContextValue;
	children: ReactNode;
}) {
	const memoizedValue = useMemo<FeatureStateContextValue>(() => value, [value]);

	return (
		<FeatureStateContext.Provider value={memoizedValue}>
			{children}
		</FeatureStateContext.Provider>
	);
}

export function useFeatureStateContext(): FeatureStateContextValue {
	const context = useContext(FeatureStateContext);

	if (!context) {
		throw new Error(
			"useFeatureStateContext must be used within a FeatureStateProvider"
		);
	}

	return context;
}